import React, { useId } from 'react';
import clsx from 'clsx';
import { IProps } from './CTABlock.interface';
import '@/blocks/CTABlock/CTABlock.styles.css';
import BlockWrapper from '@/layouts/BlockWrapper/BlockWrapper.component';
import ContentWrapper, { ContentColumn } from '@/layouts/ContentWrapper/ContentWrapper.component';
import Heading from '@/elements/Heading/Heading.component';
import Text from '@/elements/Text/Text.component';
import Button from '@/elements/Button/Button.component';

const CTABlock: React.FC<IProps> = (props) => {
    const {
        className,
        layout = 'hero',
        title,
        description,
        buttons,
        'aria-labelledby': ariaLabelledByProp,
        'aria-describedby': ariaDescribedByProp,
        'aria-label': ariaLabelProp,
        ...blockWrapperProps
    } = props;

    const reactId = useId();
    const headingId = title ? `${reactId}-cta-heading` : undefined;
    const descriptionId = description ? `${reactId}-cta-desc` : undefined;

    const sectionLabelledBy = ariaLabelledByProp ?? headingId;
    const mergedDescribedBy = [descriptionId, ariaDescribedByProp].filter(Boolean).join(' ').trim();

    const actionCount = buttons?.length ?? 0;
    const actionsGroupProps =
        actionCount > 1
            ? {
                  role: 'group' as const,
                  'aria-labelledby': headingId,
                  ...(!headingId
                      ? { 'aria-label': ariaLabelProp ?? 'Actions for this call to action' }
                      : {}),
              }
            : {};

    return (
        <BlockWrapper
            {...blockWrapperProps}
            layout={layout}
            className={clsx('ctaBlock', className)}
            aria-labelledby={sectionLabelledBy || undefined}
            aria-describedby={mergedDescribedBy || undefined}
            aria-label={!sectionLabelledBy ? ariaLabelProp : undefined}
        >
            <ContentWrapper className="!pt-12 !px-6 !pb-20 md:!py-16">
                <ContentColumn>
                    <div className="ctaBlock__layout">
                        <div className="ctaBlock__content">
                            {title && (
                                <Heading
                                    id={headingId}
                                    level={2}
                                    className="ctaBlock__heading"
                                    html={title}
                                />
                            )}
                            {description && (
                                <div id={descriptionId}>
                                    <Text className="ctaBlock__description" html={description} />
                                </div>
                            )}
                        </div>
                        {actionCount > 0 && (
                            <div className="ctaBlock__buttons" {...actionsGroupProps}>
                                {buttons!.map((btn, idx) => (
                                    <Button
                                        key={`cta-block-button-${idx}`}
                                        {...btn}
                                        className={clsx('ctaBlock__button', btn.className)}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </ContentColumn>
            </ContentWrapper>
        </BlockWrapper>
    );
};

export { CTABlock };
